import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BeaconRange } from "@models/BeaconRange";
import client from "@services/api";

export const beaconRangesKeyFactory = createQueryKeys("beacon-ranges", {
  list: (purchaseOrderVariantId: string) => ({
    queryKey: [purchaseOrderVariantId],
    queryFn: () =>
      client
        .get<BeaconRange[]>(`/beacon-ranges`, {
          params: {
            filter: { purchaseOrderVariantId },
            skipPagination: true,
          },
        })
        .then((res) => res.data),
  }),
});

export const useBeaconRanges = (purchaseOrderVariantId: string) => {
  return useQuery(beaconRangesKeyFactory.list(purchaseOrderVariantId));
};

export const useCreateBeaconRange = (purchaseOrderVariantId: string) => {
  const queryClient = useQueryClient();
  const queryKey = beaconRangesKeyFactory.list(purchaseOrderVariantId).queryKey;

  return useMutation({
    mutationFn: (data: Omit<BeaconRange, "id" | "purchaseOrderVariantId">) =>
      client
        .post<BeaconRange>("/beacon-ranges", {
          __type: "beacon-range",
          purchaseOrderVariantId,
          ...data,
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export type UpdateBeaconRangePayload = Omit<
  BeaconRange,
  "purchaseOrderVariantId"
>;

export const useUpdateBeaconRange = (purchaseOrderVariantId: string) => {
  const queryClient = useQueryClient();
  const queryKey = beaconRangesKeyFactory.list(purchaseOrderVariantId).queryKey;

  return useMutation({
    mutationFn: (data: UpdateBeaconRangePayload) =>
      client
        .put<BeaconRange>(`/beacon-ranges/${data.id}`, {
          __type: "beacon-range",
          purchaseOrderVariantId,
          ...data,
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useDeleteBeaconRange = (purchaseOrderVariantId: string) => {
  const queryClient = useQueryClient();
  const queryKey = beaconRangesKeyFactory.list(purchaseOrderVariantId).queryKey;

  return useMutation({
    mutationFn: (id: string) =>
      client.delete(`/beacon-ranges/${id}`).then(() => id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
